import {
    convertPageBaseLayerToPopupBaseLayer,
    duplicateBaseLayer,
    findName,
    getRootNode,
    layerInitialize
} from "@builder/store/slices/BuilderSlice/builderHelpers";
import { BuilderState } from "@builder/store/slices/BuilderSlice/types";
import { BaseLayer, BaseLayerTypes, Build, SpecialLayers } from "@common/build/types";
import { PayloadAction } from "@reduxjs/toolkit";
import { v4 } from "uuid";
import applyDefaultStyles from "./newNodeDefaultStyle";

//Page Reducers
export const setCurrentLayer = (
    state: BuilderState,
    action: PayloadAction<{
        type: BaseLayerTypes;
        index: number;
    }>
) => {
    state.currentLayer = action.payload;
};


const doesHomepageStillExists = (state: BuilderState, type: BaseLayerTypes) => {
    const homepage = type === BaseLayerTypes.PAGES ? state.build.specialLayers?.homepage : state.build.specialLayers?.customerDisplayHomepage;
    const relevantPages = type === BaseLayerTypes.PAGES ? state.build.pages : state.build.cfds;
    if (homepage && relevantPages.map(page => page.id).includes(homepage.id)) {
        return true;
    }
    return false;
}

export const addLayer = (state: BuilderState, action: PayloadAction<{ name: string; type: BaseLayerTypes }>) => {
    const newName = findName(state.build[action.payload.type], action.payload.name);
    const newLayer = layerInitialize(action.payload.type, newName);
    const root = getRootNode(newLayer);
    applyDefaultStyles(root, state);
    state.build[action.payload.type].push(newLayer);
    state.selectedNode = null;
    state.hoveredNode.id = null;
    state.currentLayer = {
        type: action.payload.type,
        index: state.build[action.payload.type].length - 1
    };

    if (action.payload.type === BaseLayerTypes.CFDS && state.build.cfds.length === 1 && state.build.specialLayers) {
        state.build.specialLayers.customerDisplayHomepage = {
            id: state.build.cfds[0].id,
            name: "Home",
            type: BaseLayerTypes.CFDS
        };
    }
};

export const deleteLayer = (state: BuilderState, action: PayloadAction<{ type: BaseLayerTypes; index: number }>) => {
    if (
        (state.build[action.payload.type].length > 1 || action.payload.type !== BaseLayerTypes.PAGES) &&
        state.build[action.payload.type].length > action.payload.index
    ) {
        state.build[action.payload.type].splice(action.payload.index, 1);
        state.currentLayer = {
            type: state.build[action.payload.type].length > 0 ? action.payload.type : BaseLayerTypes.PAGES,
            index: 0
        };

        if (state?.build?.specialLayers && !doesHomepageStillExists(state, action.payload.type)) {
            if (action.payload.type === BaseLayerTypes.PAGES) {
                state.build.specialLayers.homepage = {
                    id: state.build.pages[0].id,
                    name: "Home",
                    type: BaseLayerTypes.PAGES
                };
            }
            else if (action.payload.type === BaseLayerTypes.CFDS && state.build.cfds.length > 0) {
                state.build.specialLayers.customerDisplayHomepage = {
                    id: state.build.cfds[0].id,
                    name: "Home",
                    type: BaseLayerTypes.CFDS
                };
            }

           
        }
    }
};

export const setLayerName = (state: BuilderState, action: PayloadAction<{ type: BaseLayerTypes; index: number; name: string }>) => {
    if (state.build[action.payload.type].length > action.payload.index) {
        if (state.build[action.payload.type][action.payload.index].name !== action.payload.name) {
            const newName = findName(state.build[action.payload.type], action.payload.name);
            state.build[action.payload.type][action.payload.index].name = newName;
        }
    }
};

export const duplicateLayer = (state: BuilderState, action: PayloadAction<{ type: BaseLayerTypes; index: number }>) => {
    if (state.build[action.payload.type].length >= action.payload.index) {
        const newLayer = duplicateBaseLayer(state.build[action.payload.type][action.payload.index]);
        newLayer.name = findName(state.build[action.payload.type], newLayer.name, true);
        state.build[action.payload.type].splice(action.payload.index + 1, 0, newLayer);
    }
};

export const setLayoutToCurrentPage = (state: BuilderState, action: PayloadAction<{ layout: BaseLayer }>) => {
    if (state.currentLayer.type === BaseLayerTypes.PAGES) {
        const oldName = state.build.pages[state.currentLayer.index].name;
        const oldID = state.build.pages[state.currentLayer.index].id;
        state.build.pages[state.currentLayer.index] = duplicateBaseLayer(action.payload.layout);
        state.build.pages[state.currentLayer.index].name = oldName;
        state.build.pages[state.currentLayer.index].id = oldID;
    } else if (state.currentLayer.type === BaseLayerTypes.POPUPS) {
        const oldName = state.build.popups[state.currentLayer.index].name;
        const oldID = state.build.popups[state.currentLayer.index].id;
        const newBaseLayer = convertPageBaseLayerToPopupBaseLayer(action.payload.layout);
        newBaseLayer.name = oldName;
        newBaseLayer.id = oldID;
        state.build.popups[state.currentLayer.index] = newBaseLayer;
    } else if (state.currentLayer.type === BaseLayerTypes.CFDS) {
        state.build.cfds[state.currentLayer.index] = duplicateBaseLayer(action.payload.layout);
        const oldName = state.build.cfds[state.currentLayer.index].name;
        const oldID = state.build.cfds[state.currentLayer.index].id;
        state.build.cfds[state.currentLayer.index].name = oldName;
        getRootNode(state.build.cfds[state.currentLayer.index]).name = "baseLayerCFD";
        state.build.cfds[state.currentLayer.index].id = oldID;
    }
};

export const setSpecialLayers = (state: BuilderState, action: PayloadAction<SpecialLayers>) => {
    state.build.specialLayers = { ...state.build.specialLayers, ...action.payload };
};

export const removeSpecialLayer = (state: BuilderState, action: PayloadAction<"homepage" | "checkoutComplete" | "customerDisplayHomepage">) => {
    if (state.build.specialLayers?.[action.payload]) {
        delete state.build.specialLayers[action.payload];
    }
};

export const copyDesignSetLayer = (state: BuilderState, action: PayloadAction<{ layer: BaseLayer; type: BaseLayerTypes }>) => {
    const newLayer = {
        ...action.payload.layer,
        id: v4(),
        name:
            state.currentLayer.type === action.payload.type
                ? state.build[state.currentLayer.type][state.currentLayer.index].name
                : findName(state.build[action.payload.type], action.payload.layer.name)
    };
    if (state.currentLayer.type === action.payload.type) {
        if (state.build[action.payload.type][state.currentLayer.index]) {
            state.build[action.payload.type][state.currentLayer.index] = newLayer;
        } else {
            state.build[action.payload.type].push(newLayer);
            state.currentLayer = { type: action.payload.type, index: state.build[action.payload.type].length - 1 };
        }
    } else {
        if (state.build[action.payload.type]) {
            state.build[action.payload.type].push(newLayer);
        } else {
            state.build[action.payload.type] = [newLayer];
        }
        state.currentLayer = { type: action.payload.type, index: state.build[action.payload.type].length - 1 };
    }
};

export const installDesignSet = (state: BuilderState, action: PayloadAction<Build>) => {
    state.build = {
        ...state.build,
        ...action.payload,
        id: state.build.id,
        buildType: state.build.buildType
    };
    state.currentLayer = {
        index: 0,
        type: BaseLayerTypes.PAGES
    };
};
